import { of, switchMap, tap, merge, fromEvent, Subject, ReplaySubject } from 'rxjs';

import { replaceViewContainer, renderTemplate } from './ui.js';
import { getCollectionMetadata, listCollectionApiKeys, createCollectionApiKey, deleteCollectionApiKey } from './knowledge-api.js';

let mcpBaseUrl = 'https://gpt.ma300k.de/api';

function buildClaudeDesktopConfig(colId) {
    return JSON.stringify({
        mcpServers: {
            'm3k-gpt-knowledge-collection': {
                url: `${mcpBaseUrl}/mcp/collection/${colId}`,
                headers: {
                    Authorization: 'Bearer YOUR_API_KEY',
                },
            },
        },
    }, null, 2);
}

function buildOpenCodeConfig(colId) {
    return JSON.stringify({
        $schema: 'https://opencode.ai/config.json',
        mcp: {
            'm3k-gpt-knowledge-collection': {
                type: 'remote',
                url: `${mcpBaseUrl}/mcp/collection/${colId}`,
                headers: {
                    Authorization: 'Bearer YOUR_API_KEY',
                },
            },
        },
    }, null, 2);
}

function buildCursorConfig(colId) {
    return JSON.stringify({
        mcpServers: {
            'm3k-gpt-knowledge-collection': {
                url: `${mcpBaseUrl}/mcp/collection/${colId}`,
                headers: {
                    Authorization: 'Bearer YOUR_API_KEY',
                },
            },
        },
    }, null, 2);
}

function formatDate(dateStr) {
    let d = new Date(dateStr);
    let yyyy = d.getFullYear();
    let mm = String(d.getMonth() + 1).padStart(2, '0');
    let dd = String(d.getDate()).padStart(2, '0');
    let hh = String(d.getHours()).padStart(2, '0');
    let min = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

export function createCollectionApiKeysView(colId) {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('collection-api-keys-view');
            replaceViewContainer(root);

            root.querySelector('.mcp-config-claude-desktop').textContent = buildClaudeDesktopConfig(colId);
            root.querySelector('.mcp-config-cursor').textContent = buildCursorConfig(colId);
            root.querySelector('.mcp-config-opencode').textContent = buildOpenCodeConfig(colId);

            let reloadTrigger$ = new ReplaySubject(1);

            let renderMeta$ = getCollectionMetadata(colId).pipe(
                tap(meta => {
                    root.querySelector('.collection-title').textContent = `${meta.owner}/${meta.name}`;
                }),
            );

            let keysContainer = root.querySelector('.api-keys-container');

            let renderKeys$ = reloadTrigger$.pipe(
                switchMap(() => listCollectionApiKeys(colId)),
                tap(keys => {
                    keysContainer.innerHTML = '';
                    if (keys.length === 0) {
                        keysContainer.textContent = 'No API keys yet.';
                        return;
                    }
                    for (let key of keys) {
                        let keyEl = renderTemplate('api-key-widget');
                        keyEl.setAttribute('data-key-id', key.id);
                        keyEl.querySelector('.key-label').textContent = key.label || '(no label)';
                        keyEl.querySelector('.key-created').textContent = `created: ${formatDate(key.created)}`;
                        keyEl.querySelector('.key-last-used').textContent = key.last_used
                            ? `last used: ${formatDate(key.last_used)}`
                            : 'never used';
                        keyEl.querySelector('.delete-key').addEventListener('click', () => {
                            deleteCollectionApiKey(colId, key.id).subscribe({
                                next: () => reloadTrigger$.next(),
                                error: err => console.error('Failed to delete API key:', err),
                            });
                        }, false);
                        keysContainer.appendChild(keyEl);
                    }
                }),
            );

            let newKeyCreatedEl = root.querySelector('.new-key-created');
            let newKeyValueEl = root.querySelector('.new-key-value');

            let form = root.querySelector('form[name=createKeyForm]');
            let createKey$ = fromEvent(form, 'submit').pipe(
                switchMap(event => {
                    event.preventDefault();
                    let label = (form.label.value || '').trim() || null;
                    return createCollectionApiKey(colId, label);
                }),
                tap(result => {
                    newKeyValueEl.textContent = result.key;
                    newKeyCreatedEl.classList.remove('hidden');
                    form.reset();
                    reloadTrigger$.next();
                }),
            );

            reloadTrigger$.next();

            return merge(
                renderMeta$,
                renderKeys$,
                createKey$,
            );
        }),
    );
}
